import createGlobe from "cobe";
import type { GlobeData } from "../shared/types";

export type { GlobeData, GlobeMarker } from "../shared/types";

type RGB = [number, number, number];

export interface UmamiGlobeOptions {
  /** URL of the JSON endpoint created with `createGlobeHandler`. Defaults to "/api/visitor-globe". */
  endpoint?: string;
  /** Pass data directly instead of fetching from `endpoint`. */
  data?: GlobeData;
  /** Globe size in CSS pixels. Defaults to 250. */
  size?: number;
  /** Dark color scheme. Defaults to false. */
  dark?: boolean;
  /** Globe rotation speed (radians per frame). Defaults to 0.004. Set 0 to disable. */
  rotateSpeed?: number;
  /** Land dot color. */
  baseColor?: RGB;
  /** Visitor marker color. Defaults to orange. */
  markerColor?: RGB;
  /** Atmosphere glow color. */
  glowColor?: RGB;
  /** Show the "N visitors from M countries" caption. Defaults to true. Pass a function to customize the text. */
  caption?: boolean | ((data: GlobeData) => string);
  /** Called once visitor data has been loaded. */
  onData?: (data: GlobeData) => void;
}

export interface UmamiGlobeInstance {
  destroy: () => void;
}

const EMPTY_DATA: GlobeData = { markers: [], totalVisitors: 0, totalCountries: 0 };

function defaultCaption(data: GlobeData): string {
  return `${data.totalVisitors.toLocaleString()} visitors from ${data.totalCountries} countries`;
}

/**
 * Render the visitor globe into a container element.
 * Returns an instance with a `destroy()` method.
 */
export function renderUmamiGlobe(
  container: HTMLElement,
  options: UmamiGlobeOptions = {},
): UmamiGlobeInstance {
  const {
    endpoint = "/api/visitor-globe",
    size = 250,
    dark = false,
    rotateSpeed = 0.004,
    markerColor = [0.98, 0.55, 0.15],
    baseColor = dark ? [0.25, 0.32, 0.45] : [0.55, 0.7, 0.95],
    glowColor = dark ? [0.1, 0.13, 0.2] : [0.85, 0.9, 1],
    caption = true,
    onData,
  } = options;

  let destroyed = false;
  let cleanupGlobe: (() => void) | null = null;

  const wrapper = document.createElement("div");
  wrapper.style.cssText =
    "display:flex;flex-direction:column;align-items:center;gap:4px;width:100%;";

  const canvas = document.createElement("canvas");
  canvas.width = size * 2;
  canvas.height = size * 2;
  canvas.style.cssText = `width:${size}px;height:${size}px;max-width:100%;aspect-ratio:1;`;
  canvas.setAttribute("aria-label", "Visitor globe");
  wrapper.append(canvas);

  const captionEl = document.createElement("p");
  captionEl.style.cssText =
    "margin:0;font-size:12px;opacity:.65;text-align:center;font-family:inherit;";
  if (caption) wrapper.append(captionEl);

  container.append(wrapper);

  function start(data: GlobeData) {
    if (destroyed) return;

    if (caption && data.totalVisitors > 0) {
      captionEl.textContent =
        typeof caption === "function" ? caption(data) : defaultCaption(data);
    }
    onData?.(data);

    let phi = 0;
    let frameId = 0;

    const globe = createGlobe(canvas, {
      devicePixelRatio: 2,
      width: size * 2,
      height: size * 2,
      phi: 0,
      theta: 0.2,
      dark: dark ? 1 : 0,
      diffuse: 1.2,
      mapSamples: 16000,
      mapBrightness: dark ? 4 : 7,
      baseColor,
      markerColor,
      glowColor,
      markers: data.markers,
    });

    if (rotateSpeed > 0) {
      const rotate = () => {
        phi += rotateSpeed;
        globe.update({ phi });
        frameId = requestAnimationFrame(rotate);
      };
      frameId = requestAnimationFrame(rotate);
    }

    cleanupGlobe = () => {
      cancelAnimationFrame(frameId);
      globe.destroy();
    };
  }

  if (options.data) {
    start(options.data);
  } else {
    fetch(endpoint)
      .then((res) => (res.ok ? res.json() : EMPTY_DATA))
      .catch(() => EMPTY_DATA)
      .then((data: GlobeData) => start(data));
  }

  return {
    destroy() {
      destroyed = true;
      cleanupGlobe?.();
      wrapper.remove();
    },
  };
}
